import {
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import { getEncounterAbilityAssignmentGroups } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'

type EncounterEntry = {
  id?: string
  time: number
  duration: number
  phaseIndex: number
}

type EncounterAbility = {
  spell_id: number
  name?: string | null
  color: string
  entries: EncounterEntry[]
}

type EncounterDifficultyLike = {
  abilities: Record<string, EncounterAbility>
}

export type EncounterAssignmentRow = {
  abilityKey: RaidPlanEncounterAssignmentAbilityKey
  spellId: number
  name: string
  color: string
  entries: {
    id: string
    time: number
    duration: number
    castKey: string
    phaseIndex: number
  }[]
  assignmentGroups: number
  first: number
}

type ResolveEncounterAssignmentRowsOptions = {
  encounterDifficulty: EncounterDifficultyLike
  defaultPhaseStarts: number[]
  effectivePhaseDurations: number[]
  phaseStarts: number[]
  phaseEnds: number[]
  unknownAbilityLabel: string
  customAbilityLabel?: string
  abilityOverridesByKey?: Record<
    RaidPlanEncounterAssignmentAbilityKey,
    RaidPlanEncounterAssignmentAbility
  >
}

const toOverrideTime = (
  value: unknown,
  phaseStart: number,
  phaseEnd: number,
) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  const maxTime = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
  return Math.floor(Math.max(Math.min(value, maxTime), phaseStart))
}

const isStandardAbilityKey = (abilityKey: string) => /^\d+$/.test(abilityKey)

export const resolveEncounterAssignmentRows = ({
  encounterDifficulty,
  defaultPhaseStarts,
  effectivePhaseDurations,
  phaseStarts,
  phaseEnds,
  unknownAbilityLabel,
  customAbilityLabel = 'Custom',
  abilityOverridesByKey,
}: ResolveEncounterAssignmentRowsOptions): EncounterAssignmentRow[] => {
  const resolveCustomEntries = (
    castOverrides: NonNullable<
      RaidPlanEncounterAssignmentAbility['cast_overrides']
    >,
    requirePhaseDuration: boolean,
  ) =>
    Object.entries(castOverrides)
      .flatMap(([castId, override]) => {
        if (!override?.custom || override.ignored) return []
        const phaseIndex = override.phase_index
        if (
          typeof phaseIndex !== 'number' ||
          !Number.isInteger(phaseIndex) ||
          phaseIndex < 0
        ) {
          return []
        }
        const phaseStart = phaseStarts[phaseIndex]
        const phaseEnd = phaseEnds[phaseIndex]
        const phaseDuration = effectivePhaseDurations[phaseIndex]
        if (
          phaseStart === undefined ||
          phaseEnd === undefined ||
          (requirePhaseDuration && phaseDuration === undefined)
        ) {
          return []
        }
        const time = toOverrideTime(override.time, phaseStart, phaseEnd)
        if (time === undefined) return []
        return [
          {
            id: castId,
            time,
            duration: 0.01,
            phaseIndex,
          },
        ]
      })
      .sort((left, right) => left.time - right.time)

  const abilityOrder = Object.keys(abilityOverridesByKey ?? {})
  const abilityOrderIndexByKey = abilityOrder.reduce<Record<string, number>>(
    (acc, abilityKey, index) => {
      acc[abilityKey] = index
      return acc
    },
    {},
  )
  const standardRows = Object.values(encounterDifficulty.abilities)
    .map((ability) => {
      const abilityOverride =
        abilityOverridesByKey?.[ability.spell_id.toString()]?.cast_overrides ??
        {}
      const defaultEntries = ability.entries.flatMap((entry) => {
        const phaseStart = phaseStarts[entry.phaseIndex]
        const phaseEnd = phaseEnds[entry.phaseIndex]
        const phaseDuration = effectivePhaseDurations[entry.phaseIndex]
        if (
          phaseStart === undefined ||
          phaseEnd === undefined ||
          phaseDuration === undefined
        ) {
          return []
        }
        if (entry.time > phaseDuration || entry.time < 0) return []
        if (entry.id && abilityOverride[entry.id]?.ignored) return []
        const baseTime = Math.floor(phaseStart + entry.time)
        const overrideTime = entry.id
          ? toOverrideTime(
              abilityOverride[entry.id]?.time,
              phaseStart,
              phaseEnd,
            )
          : undefined
        const time = overrideTime ?? baseTime
        const maxDuration = phaseEnd - time
        const duration = Math.max(Math.min(entry.duration, maxDuration), 0)
        if (duration <= 0) return []
        return [
          {
            id:
              entry.id ??
              `${ability.spell_id}:${entry.phaseIndex}:${entry.time}`,
            time,
            duration,
            phaseIndex: entry.phaseIndex,
          },
        ]
      })
      const customEntries = resolveCustomEntries(abilityOverride, true)
      const entries = [...defaultEntries, ...customEntries].sort(
        (left, right) => left.time - right.time,
      )
      const originalFirst = ability.entries
        .map((entry) => {
          const phaseStart = defaultPhaseStarts[entry.phaseIndex] ?? 0
          return phaseStart + entry.time
        })
        .sort((a, b) => a - b)[0]
      return {
        abilityKey: ability.spell_id.toString(),
        spellId: ability.spell_id,
        name: ability.name || unknownAbilityLabel,
        color: ability.color,
        entries: entries.map((entry, index) => ({
          ...entry,
          castKey: entry.id || `${ability.spell_id}:${index}`,
        })),
        assignmentGroups: getEncounterAbilityAssignmentGroups(ability.spell_id),
        first: originalFirst ?? entries[0]?.time ?? 0,
      }
    })
    .sort((left, right) => left.first - right.first)
    .map((row) => ({
      ...row,
      assignmentGroups: Math.max(row.assignmentGroups, 1),
    }))

  const customRows = Object.entries(abilityOverridesByKey ?? {})
    .flatMap(([abilityKey, ability]) => {
      if (isStandardAbilityKey(abilityKey)) return []
      const castOverrides = ability.cast_overrides ?? {}
      const entries = resolveCustomEntries(castOverrides, false).map(
        (entry) => ({
          ...entry,
          castKey: entry.id,
        }),
      )
      const first = entries[0]?.time ?? Number.MAX_SAFE_INTEGER
      return [
        {
          abilityKey,
          spellId: ability.spell_id,
          name: ability.title?.trim() || customAbilityLabel,
          color: ability.rgb ?? 'rgb(255, 255, 255)',
          entries,
          assignmentGroups: getEncounterAbilityAssignmentGroups(
            ability.spell_id,
          ),
          first,
        },
      ]
    })
    .sort((left, right) => {
      const leftOrder = abilityOrderIndexByKey[left.abilityKey]
      const rightOrder = abilityOrderIndexByKey[right.abilityKey]
      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder
      }
      if (leftOrder !== undefined) return -1
      if (rightOrder !== undefined) return 1
      return left.first - right.first
    })
    .map((row) => ({
      ...row,
      assignmentGroups: Math.max(row.assignmentGroups, 1),
    }))

  return [...standardRows, ...customRows]
}
